import { getDate } from "medere-ui-framework";
import {
    BudgetDetailDTO,
    DEFAULT_HEALTH_ENTITY_PERCENTAGE_COVERAGE,
    DEFAULT_PATIENT_PERCENTAGE_COVERAGE,
    HospitalizationDaysBudgetDetailDTO,
    ProfessionalActivityBudgetDetailDTO,
    calculateDistributionPercentages,
} from "../../../dto/budget/BudgetDetailDTO";
import { NomenclatorValueDTO } from "../../../dto/NomenclatorDTO";
import { getNomenclatorValueForHospitalization } from "../../../servicesHooks/BillHospitalization/useBillHospitalizationService";
import { allExpenses, getFirstHealthPatientService, withLineValue } from "./billItems";

export interface ContractContext {
    date: Date | null | undefined;
    healthEntityPlan: number | null | undefined;
    billingEntity: number | null | undefined;
    /** La factura tiene internación (si no, el paciente es solo quirúrgico y se valoriza como ambulatorio). */
    hasHospitalization: boolean;
}

/** Hace falta plan y fecha para pedir el valor del convenio. */
export const canValueByContract = (ctx: ContractContext): boolean =>
    Boolean(ctx.healthEntityPlan && ctx.healthEntityPlan > 0 && ctx.date);

/** Valor del convenio de una práctica o estadía. `surgeryId` deja que el backend decida la modalidad. */
export function fetchContractValue(nomenclator: number, ctx: ContractContext, surgeryId?: number | null): Promise<NomenclatorValueDTO | null> {
    return getNomenclatorValueForHospitalization(
        nomenclator,
        getDate(ctx.date) ?? undefined,
        ctx.healthEntityPlan ?? undefined,
        ctx.billingEntity,
        ctx.hasHospitalization,
        surgeryId
    );
}

const coverageFrom = (value: NomenclatorValueDTO | null) => ({
    healthentitypercentagecoverage: value?.coveragePercentage ?? DEFAULT_HEALTH_ENTITY_PERCENTAGE_COVERAGE,
    patientpercentagecoverage: value?.coveragePercentage != null ? 100 - value.coveragePercentage : DEFAULT_PATIENT_PERCENTAGE_COVERAGE,
});

/** Campos de una práctica valorizada por el convenio (precio, honorarios, derechos y cobertura). */
export function professionalActivityValue(value: NomenclatorValueDTO | null): Partial<ProfessionalActivityBudgetDetailDTO> {
    const amount = value?.amount ?? 0;
    const { medicalPercentage, expensesPercentage } = calculateDistributionPercentages(value?.medicalAmount ?? 0, value?.expensesAmount ?? 0);
    return {
        unitprice: amount,
        medicalamount: amount > 0 ? value?.medicalAmount ?? 0 : 0,
        expensesamount: amount > 0 ? value?.expensesAmount ?? 0 : 0,
        medicalpercentage: medicalPercentage,
        expensespercentage: expensesPercentage,
        ...coverageFrom(value),
    };
}

export function hospitalizationDaysValue(value: NomenclatorValueDTO | null): Partial<HospitalizationDaysBudgetDetailDTO> {
    const amount = value?.amount ?? 0;
    return { unitprice: amount, ...allExpenses(amount), ...coverageFrom(value) };
}

export type RevaluationPatch<T> = { index: number; nomenclator: number; patch: Partial<T> };

/**
 * Revaloriza las prácticas con el convenio (al cambiar la entidad facturadora o al traer los ítems del período).
 * Las prácticas de cirugía mandan su servicio para que el backend decida la modalidad, como antes.
 * TODO(novarium-dev): es un pedido por práctica; conviene un endpoint que valorice en lote.
 */
export async function revalueProfessionalActivities(items: ProfessionalActivityBudgetDetailDTO[], ctx: ContractContext): Promise<RevaluationPatch<ProfessionalActivityBudgetDetailDTO>[]> {
    if (!canValueByContract(ctx)) return [];
    const results = await Promise.all(items.map(async (item, index) => {
        if (!(item.nomenclator > 0)) return null;
        const value = await fetchContractValue(item.nomenclator, ctx, getFirstHealthPatientService(item));
        if (value?.amount == null) return null;
        return { index, nomenclator: item.nomenclator, patch: professionalActivityValue(value) };
    }));
    return results.filter((r): r is RevaluationPatch<ProfessionalActivityBudgetDetailDTO> => r !== null);
}

/** Igual para las estadías. Monto 0 o nulo = el convenio no define precio: se respeta el que ya tiene. */
export async function revalueHospitalizationDays(items: HospitalizationDaysBudgetDetailDTO[], ctx: ContractContext): Promise<RevaluationPatch<HospitalizationDaysBudgetDetailDTO>[]> {
    if (!canValueByContract(ctx)) return [];
    const results = await Promise.all(items.map(async (item, index) => {
        if (!(item.nomenclator > 0)) return null;
        const value = await fetchContractValue(item.nomenclator, ctx);
        if (!value?.amount) return null;
        return { index, nomenclator: item.nomenclator, patch: hospitalizationDaysValue(value) };
    }));
    return results.filter((r): r is RevaluationPatch<HospitalizationDaysBudgetDetailDTO> => r !== null);
}

/** Aplica los cambios sobre el estado actual, solo si el ítem sigue en el mismo lugar. Con el precio nuevo, recalcula el importe. */
export function applyRevaluation<T extends BudgetDetailDTO & { nomenclator: number }>(items: T[], patches: RevaluationPatch<T>[]): T[] {
    if (patches.length === 0) return items;
    const next = [...items];
    patches.forEach(({ index, nomenclator, patch }) => {
        if (next[index]?.nomenclator === nomenclator) next[index] = withLineValue({ ...next[index], ...patch });
    });
    return next;
}
